/**
 * WhatsApp inbox (A13 epic) via /v1/whatsapp-inbox/conversations: conversation list with tabs, thread with bubbles, composer, AI drafts
 * (approve and send / edit) and the lead panel (/v1/whatsapp-leads/leads/:id). Contact names and message text are
 * untrusted: bidi controls are stripped and each run is isolated with <bdi> so RTL-override spoofing
 * cannot reorder what the agent sees (QM-NX-017).
 * "Return to AI" (POST .../return-to-ai; 402 = add-on off or limit reached) and the inbox-wide AI replies setting
 * (GET/PUT /v1/whatsapp-inbox/settings; editing is owner/admin only) sit next to the take-over control and above the panes.
 * Below them, a collapsible "AI assistant" section (AssistantSettings.tsx): name, tone, greeting, business hours, FAQs, handover.
 */
import { useState } from 'react';
import type { Translate } from '@qm/i18n';
import { EmptyState, ErrorState, Heading, Loading, PageHeader, Text } from '@qm/ui';
import { useApp } from '../../../lib/app-context';
import { BffError, bffJson } from '../../../lib/bff';
import { useFormat } from '../../../lib/format';
import { useResource } from '../../../lib/resource';
import { rowsOf, str, useWorkspaceName } from '../_shared/ListScreen';
import '../_shared/customer.css';
import './inbox-preview.css';
import { InboxPreview } from './InboxPreview';
import { BrandLogo } from '../../../shell/BrandLogo';
import { AssistantSettingsPanel } from './AssistantSettings';
import { AI_MODES, aiModeOf, canEditAiMode, canReturnToAi, INBOX_TABS, inTab, qualificationRows, tabCounts, type AiMode, type InboxTab } from './inbox-data';

interface Message { id: string; direction: 'in' | 'out'; text?: string; body?: string; at?: string; createdAt?: string }
interface Draft { id: string; body?: string | null }
interface Detail { id?: string; state?: string; outboundSeq?: number; leadId?: string | null; assignedTo?: string | null; contactName?: string | null; messages?: Message[]; drafts?: Draft[] }
interface LeadView { id: string; stage?: string; assignedTo?: string | null; qualification?: { captured: number; required: number; percent: number }; fields?: Record<string, unknown> }
interface Member { id: string; userId?: string; email?: string; displayName?: string | null; name?: string | null; status?: string }

export function InboxPage() {
  const { t } = useApp();
  const f = useFormat();
  const list = useResource<unknown>('/v1/whatsapp-inbox/conversations');
  const [active, setActive] = useState<string | null>(null);
  const [tab, setTab] = useState<InboxTab>('open');
  /** Per-session UI state: sample data is shown only after the explicit click, never stored. */
  const [previewing, setPreviewing] = useState(false);
  const thread = useResource<Detail>(active ? `/v1/whatsapp-inbox/conversations/${encodeURIComponent(active)}` : null);
  const rows = list.status === 'ready' ? rowsOf(list.data) : [];
  const counts = tabCounts(rows);
  const shown = rows.filter((r) => inTab(r, tab));
  const detail = thread.status === 'ready' ? thread.data : null;
  const leadId = detail?.leadId ?? null;
  const lead = useResource<LeadView>(leadId ? `/v1/whatsapp-leads/leads/${encodeURIComponent(leadId)}` : null);
  const team = useResource<{ members?: Member[] }>(active ? '/v1/team/members' : null);
  const reloadAll = () => { thread.reload(); list.reload(); lead.reload(); };
  return (
    <section className="qm-page cu-inbox" aria-labelledby="page-title">
      <PageHeader eyebrow={useWorkspaceName()} title={t('shell.screens.inbox.title')} intro={t('shell.screens.inbox.intro')} />
      {previewing ? null : <AiRepliesSetting />}
      {previewing ? null : <AssistantSection />}
      {list.status === 'loading' ? <Loading label={t('common.loading')} /> : list.status === 'error' ? <ErrorState message={t('shell.error.load')} /> : !rows.length ? (previewing ? <InboxPreview onExit={() => setPreviewing(false)} /> : (
        <EmptyState title={t('shell.screens.inbox.empty')}>
          <div className="cu-empty-preview"><Text tone="muted">{t('whatsapp-inbox.preview.hint')}</Text><button type="button" className="cu-btn" data-variant="outline" onClick={() => setPreviewing(true)}>{t('whatsapp-inbox.preview.cta')}</button></div>
        </EmptyState>
      )) : (
        <div className="qs-inbox">
          <div>
            <div className="cu-tabs" role="group" aria-label={t('whatsapp-inbox.pane.tabs')}>
              {INBOX_TABS.map((k) => <button key={k} type="button" aria-pressed={tab === k} onClick={() => setTab(k)}>{t(`whatsapp-inbox.pane.tab.${k}`)} <span aria-hidden="true">{f.number(counts[k])}</span></button>)}
            </div>
            <nav aria-label={t('shell.screens.inbox.conversations')}>
              {shown.length ? (
                <ul className="qs-inbox__list cu-convlist">
                  {shown.map((r) => (
                    <li key={r.id} data-unread={r.unread ? 'true' : undefined}>
                      <button type="button" className="qs-inbox__row" aria-pressed={active === r.id} aria-current={active === r.id ? 'true' : undefined} onClick={() => setActive(r.id)}>
                        <span className="qs-avatar" aria-hidden="true">{[...f.text(r.contactName ?? r.name ?? r.phoneMasked)][0]?.toUpperCase() ?? ''}</span>
                        <span><span className="cu-conv-name"><bdi>{f.text(r.contactName ?? r.name ?? r.phoneMasked)}</bdi></span>
                          <small>{t(`whatsapp-inbox.state.${str(r.state) || 'ai_active'}`)}</small></span>
                        <span className="cu-conv-time">{f.time(str(r.lastMessageAt))}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              ) : <Text tone="muted">{t('whatsapp-inbox.pane.tabEmpty')}</Text>}
            </nav>
          </div>
          <div className="cu-thread" aria-live="polite">
            {!active ? <div className="qs-inbox__thread"><Text tone="muted">{t('shell.screens.inbox.pick')}</Text></div> : thread.status === 'loading' ? <div className="qs-inbox__thread"><Loading label={t('common.loading')} /></div> : thread.status === 'error' ? <div className="qs-inbox__thread"><ErrorState message={t('shell.error.load')} /></div> : (
              <ThreadPane id={active} detail={thread.data} onChanged={reloadAll} />
            )}
          </div>
          <aside className="qs-inbox__lead" aria-label={t('whatsapp-inbox.pane.lead')}>
            {!active ? <Text tone="muted">{t('whatsapp-inbox.pane.leadPick')}</Text> : detail ? (
              <LeadPane id={active} detail={detail} lead={lead.status === 'ready' ? lead.data : null} leadState={leadId ? lead.status : 'none'} members={team.status === 'ready' ? team.data.members ?? [] : []} onChanged={reloadAll} />
            ) : null}
          </aside>
        </div>
      )}
    </section>
  );
}

function ThreadPane({ id, detail, onChanged }: { id: string; detail: Detail; onChanged: () => void }) {
  const { t } = useApp();
  const f = useFormat();
  const [text, setText] = useState('');
  const [editing, setEditing] = useState<string | null>(null);
  const [edit, setEdit] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const seq = detail.outboundSeq ?? 0;
  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true); setError(null);
    try { await fn(); onChanged(); return true; } catch (e) {
      setError(e instanceof BffError && e.status === 409 ? t('whatsapp-inbox.pane.conflict') : e instanceof BffError && e.status === 403 ? t('shell.approval.noPermission') : t('shell.error.action'));
      return false;
    } finally { setBusy(false); }
  };
  const base = `/v1/whatsapp-inbox/conversations/${encodeURIComponent(id)}` as const;
  const send = async () => { const body = text.trim(); if (body && await run(() => bffJson(`${base}/messages`, 'POST', { body, expectedOutboundSeq: seq }))) setText(''); };
  const approve = (d: Draft, body?: string) => run(() => bffJson(`${base}/drafts/${encodeURIComponent(d.id)}/approve`, 'POST', { expectedOutboundSeq: seq, ...(body ? { body } : {}) })).then((ok) => { if (ok) setEditing(null); });
  return (
    <>
      <div className="cu-thread__head"><Heading level={2}><bdi>{f.text(detail.contactName ?? '')}</bdi></Heading><span className="qs-status"><span className="qs-dot" data-tone={detail.state === 'ai_active' ? 'accent' : detail.state === 'closed' ? undefined : 'good'} aria-hidden="true" />{t(`whatsapp-inbox.state.${detail.state ?? 'ai_active'}`)}</span></div>
      <ol className="cu-thread__body">
        {(detail.messages ?? []).map((m) => (
          <li key={m.id} className="qm-bubble" data-direction={m.direction}><bdi>{f.text(m.body ?? m.text)}</bdi> <small>{f.time(m.createdAt ?? m.at)}</small></li>
        ))}
      </ol>
      {(detail.drafts ?? []).map((d) => (
        <div key={d.id} className="qs-draft" data-testid="inbox-draft">
          <span className="qs-micro">{t('whatsapp-inbox.draft.title')}</span>
          {editing === d.id ? <textarea className="cu-draft-edit" aria-label={t('whatsapp-inbox.draft.edit')} value={edit} onChange={(e) => setEdit(e.target.value)} rows={3} /> : <p><bdi>{f.text(d.body)}</bdi></p>}
          <div className="qs-draft__actions">
            <button type="button" className="cu-btn" disabled={busy || (editing === d.id && !edit.trim())} onClick={() => void approve(d, editing === d.id ? edit.trim() : undefined)}>{t('whatsapp-inbox.pane.approveSend')}</button>
            {editing === d.id ? null : <button type="button" className="cu-btn" data-variant="outline" disabled={busy} onClick={() => { setEditing(d.id); setEdit(d.body ?? ''); }}>{t('whatsapp-inbox.pane.editDraft')}</button>}
          </div>
        </div>
      ))}
      {error ? <div style={{ paddingInline: '1rem' }}><ErrorState message={error} /></div> : null}
      <form className="cu-composer" onSubmit={(e) => { e.preventDefault(); void send(); }}>
        <textarea aria-label={t('whatsapp-inbox.pane.compose')} placeholder={t('whatsapp-inbox.pane.compose')} value={text} maxLength={4096} rows={2} onChange={(e) => setText(e.target.value)} />
        <button type="submit" className="cu-btn" disabled={busy || !text.trim()}>{t('whatsapp-inbox.actions.send')}</button>
      </form>
    </>
  );
}

function LeadPane({ id, detail, lead, leadState, members, onChanged }: { id: string; detail: Detail; lead: LeadView | null; leadState: string; members: Member[]; onChanged: () => void }) {
  const { t } = useApp();
  const f = useFormat();
  const [assignee, setAssignee] = useState('');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<Note | null>(null);
  const run = async (fn: () => Promise<unknown>, okKey: string, on402?: Note) => {
    setBusy(true); setNote(null);
    try { await fn(); setNote({ ok: true, text: t(okKey) }); onChanged(); } catch (e) { setNote(leadActionNote(e, t, on402)); } finally { setBusy(false); }
  };
  const base = `/v1/whatsapp-inbox/conversations/${encodeURIComponent(id)}` as const;
  const nameOf = (uid?: string | null) => { const m = members.find((x) => (x.userId ?? x.id) === uid || x.id === uid); return m ? m.displayName || m.name || m.email || '' : ''; };
  const owner = nameOf(detail.assignedTo ?? lead?.assignedTo);
  const q = lead?.qualification;
  const rows = qualificationRows(lead?.fields);
  const assignable = members.filter((m) => (m.status ?? 'active') === 'active');
  return (
    <div className="cu-leadpanel">
      <Heading level={2}>{t('whatsapp-inbox.pane.lead')}</Heading>
      {leadState === 'loading' ? <Loading label={t('common.loading')} /> : leadState === 'error' ? <ErrorState message={t('shell.error.load')} /> : leadState === 'none' ? <Text tone="muted">{t('whatsapp-inbox.pane.noLead')}</Text> : (
        <>
          {q ? (
            <div>
              <div className="qs-pulse__head"><span className="qs-micro">{t('whatsapp-inbox.pane.qualification')}</span><b>{f.number(q.captured)}/{f.number(q.required)}</b></div>
              <div className="cu-meter" role="meter" aria-valuemin={0} aria-valuemax={100} aria-valuenow={q.percent} aria-label={t('whatsapp-inbox.pane.qualification')}><span style={{ inlineSize: `${q.percent}%` }} /></div>
            </div>
          ) : null}
          <ul className="qs-kv">
            {rows.map((r) => <li key={r.key}><span className="qs-kv__label">{t(`whatsapp-inbox.pane.field.${r.key}`)}</span><span className="qs-kv__value">{r.value ? <bdi>{f.text(r.value)}</bdi> : <small>{t('whatsapp-inbox.pane.notCaptured')}</small>}</span></li>)}
          </ul>
        </>
      )}
      <ul className="qs-kv">
        <li><span className="qs-kv__label">{t('whatsapp-inbox.pane.owner')}</span><span className="qs-kv__value">{owner ? <bdi>{owner}</bdi> : t('whatsapp-inbox.pane.unassigned')}</span></li>
        <li><span className="qs-kv__label">{t('whatsapp-inbox.pane.source')}</span><span className="qs-kv__value"><BrandLogo provider="whatsapp" size={16} decorative /> WhatsApp</span></li>
        {lead?.stage ? <li><span className="qs-kv__label">{t('whatsapp-inbox.pane.stage')}</span><span className="qs-kv__value">{lead.stage}</span></li> : null}
      </ul>
      <div className="cu-btnrow">
        <button type="button" className="cu-btn" disabled={busy || detail.state === 'human_active'} onClick={() => void run(() => bffJson(`${base}/takeover`, 'POST', {}), 'whatsapp-inbox.pane.tookOver')}>{t('whatsapp-inbox.pane.takeOver')}</button>
        <ReturnToAiButton busy={busy} state={detail.state} onClick={() => void run(() => returnToAi(id), 'whatsapp-inbox.returnToAi.done', aiPausedNote(t))} />
      </div>
      <div className="cu-field">
        <label htmlFor="inbox-assignee"><span>{t('whatsapp-inbox.pane.assignTeam')}</span></label>
        <select id="inbox-assignee" value={assignee} onChange={(e) => setAssignee(e.target.value)}>
          <option value="">{t('whatsapp-inbox.pane.choose')}</option>
          {assignable.map((m) => <option key={m.id} value={m.userId ?? m.id}>{m.displayName || m.name || m.email}</option>)}
        </select>
        <button type="button" className="cu-btn" data-variant="outline" disabled={busy || !assignee} onClick={() => void run(() => bffJson(`${base}/assign`, 'POST', { assigneeId: assignee }), 'whatsapp-inbox.pane.assigned')}>{t('whatsapp-inbox.pane.assign')}</button>
      </div>
      {lead ? <button type="button" className="cu-btn" data-variant="outline" disabled={busy} onClick={() => void run(() => bffJson(`/v1/whatsapp-leads/leads/${encodeURIComponent(lead.id)}/move-to-deal`, 'POST', {}), 'whatsapp-inbox.pane.oppCreated')}>{t('whatsapp-inbox.pane.createOpp')}</button> : null}
      {note ? <NoteLine note={note} /> : null}
    </div>
  );
}

export interface Note { ok: boolean; text: string; billing?: boolean }

/** Maps a failed lead-pane action to its note. `on402` overrides the default "paid plan" text (Return to AI uses aiPausedNote). */
export function leadActionNote(e: unknown, t: Translate, on402?: Note): Note {
  const status = e instanceof BffError ? e.status : 0;
  if (status === 402 && on402) return on402;
  return { ok: false, text: status === 403 ? t('shell.approval.noPermission') : status === 402 ? t('whatsapp-inbox.pane.planRequired') : status === 404 ? t('whatsapp-inbox.pane.oppUnavailable') : t('shell.error.action') };
}
/** 402 from return-to-ai: the WhatsApp AI add-on is off or its usage limit is reached. Links to /billing. */
export const aiPausedNote = (t: Translate): Note => ({ ok: false, text: t('whatsapp-inbox.returnToAi.paused'), billing: true });
export const returnToAi = (id: string) => bffJson(`/v1/whatsapp-inbox/conversations/${encodeURIComponent(id)}/return-to-ai`, 'POST', {});

/** A result line under the lead actions; a 402 from Return to AI also links to billing, where the add-on is managed. */
export function NoteLine({ note }: { note: Note }) {
  const { t } = useApp();
  return <p role={note.ok ? 'status' : 'alert'} className="cu-hint" data-testid="inbox-note">{note.text}{note.billing ? <> <a href="/billing">{t('whatsapp-inbox.returnToAi.billing')}</a></> : null}</p>;
}

/** Hands a human-handled conversation back to the AI. Disabled unless the conversation is human_active. */
export function ReturnToAiButton({ busy, state, onClick }: { busy: boolean; state?: string | null; onClick: () => void }) {
  const { t } = useApp();
  return <button type="button" className="cu-btn" data-variant="outline" data-testid="inbox-return-to-ai" disabled={busy || !canReturnToAi(state)} onClick={onClick}>{t('whatsapp-inbox.actions.returnToAi')}</button>;
}

/** Collapsible AI assistant settings; the form (and its two requests) loads only when opened. */
function AssistantSection() {
  const { t } = useApp();
  const [open, setOpen] = useState(false);
  return (
    <details className="cu-assistant-details" data-testid="inbox-assistant-section" onToggle={(e) => setOpen((e.currentTarget as HTMLDetailsElement).open)}>
      <summary>{t('whatsapp-inbox.assistant.title')}</summary>
      {open ? <AssistantSettingsPanel /> : null}
    </details>
  );
}

export const SETTINGS_PATH = '/v1/whatsapp-inbox/settings' as const;
/** Saves the inbox-wide AI reply mode (owner/admin only on the API). */
export const saveAiMode = (aiMode: AiMode) => bffJson<{ aiMode: AiMode }>(SETTINGS_PATH, 'PUT', { aiMode });

/** Inbox-wide AI replies setting: loads GET /settings, saves PUT /settings. Visible to everyone, editable by owner/admin. */
function AiRepliesSetting() {
  const { t, me } = useApp();
  const settings = useResource<{ aiMode?: string }>(SETTINGS_PATH);
  const [saved, setSaved] = useState<AiMode | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<Note | null>(null);
  if (settings.status === 'loading') return null;
  if (settings.status === 'error') return <p className="cu-hint" role="alert">{t('whatsapp-inbox.settings.loadError')}</p>;
  const value = saved ?? aiModeOf(settings.data);
  const change = async (next: AiMode) => {
    if (next === value) return;
    setBusy(true); setNote(null);
    try { setSaved(aiModeOf(await saveAiMode(next))); setNote({ ok: true, text: t('whatsapp-inbox.settings.saved') }); } catch (e) {
      setNote({ ok: false, text: e instanceof BffError && e.status === 409 ? t('whatsapp-inbox.settings.autoBlocked') : e instanceof BffError && e.status === 403 ? t('whatsapp-inbox.settings.readOnly') : t('shell.error.action') });
    } finally { setBusy(false); }
  };
  return <AiModeControl value={value} canEdit={canEditAiMode(me?.roles)} busy={busy} note={note} onChange={(m) => void change(m)} />;
}

/** The 3-option control (Off / Approve / Auto) with a one-line explanation per option. Read-only for non-admins. */
export function AiModeControl({ value, canEdit, busy = false, note = null, onChange }: { value: AiMode; canEdit: boolean; busy?: boolean; note?: Note | null; onChange: (m: AiMode) => void }) {
  const { t } = useApp();
  return (
    <fieldset className="cu-aimode" data-testid="inbox-ai-mode" disabled={!canEdit || busy}>
      <legend>{t('whatsapp-inbox.settings.aiMode')}</legend>
      <div className="cu-aimode__options">
        {AI_MODES.map((m) => (
          <label key={m} className="cu-aimode__option" data-checked={value === m ? 'true' : undefined}>
            <input type="radio" name="inbox-ai-mode" value={m} checked={value === m} onChange={() => onChange(m)} />
            <span><b>{t(`whatsapp-inbox.settings.aiMode.${m}`)}</b><small>{t(`whatsapp-inbox.settings.aiMode.${m}.help`)}</small></span>
          </label>
        ))}
      </div>
      {canEdit ? null : <p className="cu-hint">{t('whatsapp-inbox.settings.readOnly')}</p>}
      {note ? <NoteLine note={note} /> : null}
    </fieldset>
  );
}
